import { useCallback, useEffect, useState } from "react";
import { Plus, Printer, Download, Pencil, Trash2, Receipt, Images, FolderArchive } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MonthPicker, MONTHS } from "@/components/MonthPicker";
import { ExpenseFormDialog } from "@/components/ExpenseFormDialog";
import { ReceiptThumb } from "@/components/ReceiptThumb";
import { CategoryBadge, CategoryTotals } from "@/components/CategoryTotals";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, fmtDate, downloadFile } from "@/lib/api";

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function ExpensesPage() {
  const { user } = useAuth();
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catFilter, setCatFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/expenses", { params: ym }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [ym]);
  useEffect(load, [load]);

  const remove = async (x) => {
    if (!window.confirm(`Delete "${x.description}"?`)) return;
    try { await api.delete(`/expenses/${x.id}`); toast.success("Expense deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = () => {
    const rows = [["Date", "Description", "Category", "Amount", "Receipt"]];
    data.expenses.forEach((x) => rows.push([x.date, x.description, x.category || "Other", x.amount.toFixed(2), x.receipt_path ? "Yes" : "No"]));
    rows.push([], ["Month total", "", "", data.totals.total.toFixed(2)], [], ["By category"]);
    data.by_category.forEach((c) => rows.push(["", "", c.category, c.total.toFixed(2)]));
    const blob = new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `expenses-${ym.year}-${String(ym.month).padStart(2, "0")}.csv` });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const label = `${MONTHS[ym.month - 1]} ${ym.year}`;
  const stem = `receipts-${ym.year}-${String(ym.month).padStart(2, "0")}`;
  const [bundleBusy, setBundleBusy] = useState("");
  const bundle = async (kind) => {
    setBundleBusy(kind);
    try {
      if (kind === "zip") await downloadFile(`/expenses/receipts/zip?year=${ym.year}&month=${ym.month}`, `${stem}.zip`);
      else await downloadFile(`/expenses/receipts/sheet?year=${ym.year}&month=${ym.month}`, `${stem}.pdf`, { open: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setBundleBusy(""); }
  };
  const hasReceipts = (data?.expenses || []).some((x) => x.receipt_path);
  const shown = data ? data.expenses.filter((x) => catFilter === "all" || (x.category || "Other") === catFilter) : [];
  const shownTotal = shown.reduce((s, x) => s + x.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 print:hidden">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Expenses</h1>
          <p className="text-sm text-muted-foreground mt-1">Log what you spend and keep the receipt on the line.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="expense-export-csv-button" variant="outline" onClick={exportCsv} disabled={!data}><Download className="h-4 w-4 mr-2" />CSV</Button>
          <Button data-testid="expense-print-button" variant="outline" onClick={() => window.print()} disabled={!data}><Printer className="h-4 w-4 mr-2" />Print</Button>
          <Button data-testid="expense-receipts-sheet-button" variant="outline" onClick={() => bundle("sheet")} disabled={!hasReceipts || !!bundleBusy} title="PDF of every receipt photo, 8 per page, labeled"><Images className="h-4 w-4 mr-2" />{bundleBusy === "sheet" ? "Building..." : "Print receipts"}</Button>
          <Button data-testid="expense-receipts-zip-button" variant="outline" onClick={() => bundle("zip")} disabled={!data?.expenses?.length || !!bundleBusy} title="ZIP of receipt photos + CSV"><FolderArchive className="h-4 w-4 mr-2" />{bundleBusy === "zip" ? "Building..." : "ZIP"}</Button>
          <Button data-testid="expense-add-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"><Plus className="h-4 w-4 mr-2" />Add Expense</Button>
        </div>
      </div>

      <MonthPicker value={ym} onChange={setYm} prefix="expense" />

      {!data ? <div className="text-sm text-muted-foreground">Loading expenses...</div> : (
        <div className="print-area space-y-6">
          <div className="hidden print:block">
            <h1 className="text-2xl font-extrabold">{user?.business_name}</h1>
            <p className="text-sm text-slate-600">Expense report — {label}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="surface p-4" data-testid="expense-stat-count"><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Expenses</div><div className="font-mono font-bold text-xl mt-1">{data.totals.count}</div></div>
            <div className="surface p-4" data-testid="expense-stat-total"><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{MONTHS[ym.month - 1]} total</div><div className="font-mono font-bold text-xl mt-1 text-red-600">{money(data.totals.total)}</div></div>
            <div className="surface p-4 col-span-2 sm:col-span-1" data-testid="expense-stat-year"><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{ym.year} year-to-date</div><div className="font-mono font-bold text-xl mt-1">{money(data.year_totals.total)}</div></div>
          </div>

          <div className="surface overflow-x-auto">
            {data.by_category.length > 1 && (
              <div className="sticky left-0 flex gap-2 flex-wrap p-3 border-b border-border print:hidden">
                <button data-testid="expense-cat-filter-all" onClick={() => setCatFilter("all")} className={`h-8 px-3 rounded-full text-xs font-semibold border ${catFilter === "all" ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-border hover:bg-secondary"}`}>All</button>
                {data.by_category.map((c) => (
                  <button key={c.category} data-testid={`expense-cat-filter-${c.category.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => setCatFilter(c.category)}
                    className={`h-8 px-3 rounded-full text-xs font-semibold border ${catFilter === c.category ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-border hover:bg-secondary"}`}>
                    {c.category} · {money(c.total)}
                  </button>
                ))}
              </div>
            )}
            <table className="w-full text-xs md:text-sm print:table" data-testid="expense-table">
              <thead>
                <tr className="border-b border-border text-[11px] md:text-xs font-bold uppercase tracking-wider text-muted-foreground text-left">
                  <th className="sticky left-0 z-10 bg-card border-r border-border md:border-r-0 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)] md:shadow-none px-3 md:px-4 py-3">Description</th>
                  <th className="hidden md:table-cell print:table-cell px-3 md:px-4 py-3">Date</th>
                  <th className="hidden md:table-cell print:table-cell px-3 md:px-4 py-3">Category</th>
                  <th className="px-3 md:px-4 py-3 text-right">Amount</th>
                  <th className="px-3 md:px-4 py-3">Receipt</th>
                  <th className="px-3 md:px-4 py-3 print:hidden">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-sm text-muted-foreground" data-testid="expense-empty-state">
                    <div className="flex items-center gap-3"><Receipt className="h-5 w-5 text-emerald-600" /> No expenses logged for {label}{catFilter !== "all" ? ` in ${catFilter}` : ""}. Tap "Add Expense" to record one.</div>
                  </td></tr>
                )}
                {shown.map((x) => (
                  <tr key={x.id} data-testid={`expense-row-${x.id}`} className="border-b border-border last:border-0 hover:bg-secondary/50">
                    <td className="sticky left-0 z-10 bg-card border-r border-border md:border-r-0 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)] md:shadow-none px-3 md:px-4 py-2.5 md:py-3 max-w-[170px] md:max-w-none overflow-hidden" data-testid={`expense-desc-${x.id}`}>
                      <div className="font-medium text-sm truncate">{x.description}</div>
                      <div className="md:hidden print:hidden flex items-center gap-1.5 mt-0.5 text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(x.date)} <CategoryBadge category={x.category} /></div>
                    </td>
                    <td className="hidden md:table-cell print:table-cell px-3 md:px-4 py-2.5 md:py-3 whitespace-nowrap">{fmtDate(x.date)}</td>
                    <td className="hidden md:table-cell print:table-cell px-3 md:px-4 py-2.5 md:py-3" data-testid={`expense-category-${x.id}`}><CategoryBadge category={x.category} /></td>
                    <td className="px-3 md:px-4 py-2.5 md:py-3 text-right font-mono font-bold text-sm whitespace-nowrap">{money(x.amount)}</td>
                    <td className="px-3 md:px-4 py-2.5 md:py-3">
                      <span className="print:hidden"><ReceiptThumb expense={x} /></span>
                      <span className="hidden print:inline text-xs">{x.receipt_path ? "Yes" : "—"}</span>
                    </td>
                    <td className="px-3 md:px-4 py-2.5 md:py-3 print:hidden">
                      <div className="flex items-center gap-1">
                        <button data-testid={`expense-edit-${x.id}`} onClick={() => { setEditing(x); setFormOpen(true); }} className="h-9 w-9 md:h-8 md:w-8 grid place-items-center rounded-lg hover:bg-secondary" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`expense-delete-${x.id}`} onClick={() => remove(x)} className="h-9 w-9 md:h-8 md:w-8 grid place-items-center rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {shown.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-900 dark:border-slate-200 font-bold text-sm">
                    <td className="sticky left-0 z-10 bg-card border-r border-border md:border-r-0 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)] md:shadow-none px-3 md:px-4 py-3">Total for {label}{catFilter !== "all" ? ` — ${catFilter}` : ""}</td>
                    <td className="hidden md:table-cell print:table-cell px-3 md:px-4 py-3" colSpan={2} />
                    <td className="px-3 md:px-4 py-3 text-right font-mono text-lg whitespace-nowrap" data-testid="expense-month-total">{money(catFilter === "all" ? data.totals.total : shownTotal)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 print:break-before-page">
            <CategoryTotals title={`${MONTHS[ym.month - 1]} by category`} rows={data.by_category} total={data.totals.total} testId="expense-month-categories" />
            <CategoryTotals title={`${ym.year} by category`} rows={data.year_by_category} total={data.year_totals.total} testId="expense-year-categories" />
          </div>

          <div className="surface overflow-x-auto">
            <div className="px-4 pt-4 flex items-baseline justify-between">
              <h2 className="text-lg font-semibold">{ym.year} expenses by month</h2>
              <span className="text-xs text-muted-foreground">{data.year_totals.count} entries</span>
            </div>
            <table className="w-full text-sm mt-2" data-testid="expense-year-table">
              <thead>
                <tr className="border-b border-border text-xs font-bold uppercase tracking-wider text-muted-foreground text-left">
                  <th className="px-4 py-2">Month</th><th className="px-4 py-2 text-right">Entries</th><th className="px-4 py-2 text-right">Spent</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => (
                  <tr key={m.month} data-testid={`expense-year-row-${m.month}`} onClick={() => setYm({ ...ym, month: m.month })}
                    className={`border-b border-border last:border-0 cursor-pointer hover:bg-secondary/50 ${m.month === ym.month ? "bg-emerald-50 dark:bg-emerald-950/30" : ""} ${m.count === 0 ? "text-muted-foreground" : ""}`}>
                    <td className="px-4 py-2 font-medium">{MONTHS[m.month - 1]}</td>
                    <td className="px-4 py-2 text-right font-mono">{m.count}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(m.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-900 dark:border-slate-200 font-bold">
                  <td className="px-4 py-3">Year total</td>
                  <td className="px-4 py-3 text-right font-mono">{data.year_totals.count}</td>
                  <td className="px-4 py-3 text-right font-mono" data-testid="expense-year-total">{money(data.year_totals.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <ExpenseFormDialog open={formOpen} onOpenChange={setFormOpen} expense={editing} onSaved={() => load()} />
    </div>
  );
}
